import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';

export function EmployeeForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEditing = id && id !== 'novo';
  const [tab, setTab] = useState('pessoais');

  const [form, setForm] = useState({
    name: '', cpf: '', rg: '', birth_date: '', gender: '', marital_status: '', phone: '', email: '', status: 'Ativo', photo_url: '',
    address: { zip_code: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' },
    professional: { matricula: '', admission_date: '', role: '', department: '', cost_center: '', base_salary: '' },
    labor: { ctps_number: '', ctps_series: '', pis: '' },
    banking: { bank: '', agency: '', account: '', pix: '' },
    cnh: { has_cnh: false, number: '', category: '', expiry_date: '' }
  });

  useEffect(() => {
    if (isEditing) {
      api.get(`/employees/${id}`).then(res => setForm({ ...form, ...res.data })).catch(() => toast.error('Erro ao buscar'));
    }
  }, [id]);

  const handleChange = (e, section) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    if (section) {
      setForm({ ...form, [section]: { ...form[section], [name]: val } });
    } else {
      setForm({ ...form, [name]: val });
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        return toast.error('A imagem deve ter no máximo 2MB.');
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm(prev => ({ ...prev, photo_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Nome é obrigatório.');
    try {
      if (isEditing) {
        await api.put(`/employees/${id}`, form);
        toast.success('Atualizado com sucesso!');
      } else {
        const res = await api.post('/employees', form);
        toast.success('Cadastrado com sucesso!');
        nav('/rh/funcionarios');
        return;
      }
      nav('/rh/funcionarios');
    } catch (err) {
      let msg = 'Erro ao salvar';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = 'Verifique os campos obrigatórios.';
      toast.error(msg);
    }
  };

  const tabs = [
    { id: 'pessoais', label: 'Dados Pessoais' },
    { id: 'endereco', label: 'Endereço' },
    { id: 'profissional', label: 'Profissional' },
    { id: 'trabalhista', label: 'Trabalhista' },
    { id: 'banco', label: 'Bancário' },
    { id: 'cnh', label: 'CNH (Motorista)' },
  ];

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => nav('/rh/funcionarios')}><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-2xl font-bold">{isEditing ? 'Editar Funcionário' : 'Novo Funcionário'}</h1>
        </div>
        <Button onClick={handleSave} className="bg-brand text-white hover:bg-brandDark"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
      </div>

      <div className="bg-white rounded-xl border flex overflow-hidden">
        <div className="w-64 bg-slate-50 border-r flex flex-col p-4 space-y-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-left rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-200'}`}>
              {t.label}
            </button>
          ))}
        </div>
        
        <div className="flex-1 p-8">
          {tab === 'pessoais' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 flex items-center gap-6 mb-4">
                <div className="relative w-24 h-24 rounded-full bg-slate-200 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {form.photo_url ? (
                    <img src={form.photo_url} alt="Foto" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-slate-400" />
                  )}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
                <div className="text-sm text-slate-500">
                  <p className="font-semibold text-slate-700">Foto de Perfil</p>
                  <p>Clique na imagem para enviar uma foto.</p>
                  <p>Tamanho máximo: 2MB.</p>
                </div>
              </div>
              <div className="col-span-2"><label className="block text-sm mb-1">Nome Completo *</label><input name="name" value={form.name} onChange={handleChange} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">CPF</label><input name="cpf" value={form.cpf} onChange={handleChange} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">RG</label><input name="rg" value={form.rg} onChange={handleChange} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Data de Nascimento</label><input type="date" name="birth_date" value={form.birth_date} onChange={handleChange} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Telefone / WhatsApp</label><input name="phone" value={form.phone} onChange={handleChange} className="w-full border rounded p-2" /></div>
              <div className="col-span-2"><label className="block text-sm mb-1">E-mail</label><input type="email" name="email" value={form.email} onChange={handleChange} className="w-full border rounded p-2" /></div>
              {isEditing && (
                 <div><label className="block text-sm mb-1">Status</label>
                  <select name="status" value={form.status} onChange={handleChange} className="w-full border rounded p-2">
                    <option value="Ativo">Ativo</option>
                    <option value="Férias">Férias</option>
                    <option value="Afastado">Afastado</option>
                    <option value="Desligado">Desligado</option>
                  </select>
                 </div>
              )}
            </div>
          )}
          
          {tab === 'endereco' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm mb-1">CEP</label><input name="zip_code" value={form.address.zip_code} onChange={e=>handleChange(e,'address')} className="w-full border rounded p-2" /></div>
              <div className="col-span-2"><label className="block text-sm mb-1">Logradouro</label><input name="street" value={form.address.street} onChange={e=>handleChange(e,'address')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Número</label><input name="number" value={form.address.number} onChange={e=>handleChange(e,'address')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Complemento</label><input name="complement" value={form.address.complement} onChange={e=>handleChange(e,'address')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Bairro</label><input name="neighborhood" value={form.address.neighborhood} onChange={e=>handleChange(e,'address')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Cidade</label><input name="city" value={form.address.city} onChange={e=>handleChange(e,'address')} className="w-full border rounded p-2" /></div>
            </div>
          )}

          {tab === 'profissional' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm mb-1">Matrícula</label><input name="matricula" value={form.professional.matricula} onChange={e=>handleChange(e,'professional')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Data de Admissão</label><input type="date" name="admission_date" value={form.professional.admission_date} onChange={e=>handleChange(e,'professional')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Cargo</label><input name="role" value={form.professional.role} onChange={e=>handleChange(e,'professional')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Setor</label><input name="department" value={form.professional.department} onChange={e=>handleChange(e,'professional')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Salário Base</label><input name="base_salary" value={form.professional.base_salary} onChange={e=>handleChange(e,'professional')} className="w-full border rounded p-2" /></div>
            </div>
          )}

          {tab === 'trabalhista' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm mb-1">CTPS (Número)</label><input name="ctps_number" value={form.labor.ctps_number} onChange={e=>handleChange(e,'labor')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Série</label><input name="ctps_series" value={form.labor.ctps_series} onChange={e=>handleChange(e,'labor')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">PIS</label><input name="pis" value={form.labor.pis} onChange={e=>handleChange(e,'labor')} className="w-full border rounded p-2" /></div>
            </div>
          )}

          {tab === 'banco' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm mb-1">Banco</label><input name="bank" value={form.banking.bank} onChange={e=>handleChange(e,'banking')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Agência</label><input name="agency" value={form.banking.agency} onChange={e=>handleChange(e,'banking')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Conta</label><input name="account" value={form.banking.account} onChange={e=>handleChange(e,'banking')} className="w-full border rounded p-2" /></div>
              <div><label className="block text-sm mb-1">Chave PIX</label><input name="pix" value={form.banking.pix} onChange={e=>handleChange(e,'banking')} className="w-full border rounded p-2" /></div>
            </div>
          )}

          {tab === 'cnh' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" name="has_cnh" checked={form.cnh.has_cnh} onChange={e=>handleChange(e,'cnh')} />
                  Funcionário possui CNH
                </label>
              </div>
              {form.cnh.has_cnh && (
                <>
                  <div><label className="block text-sm mb-1">Número CNH</label><input name="number" value={form.cnh.number} onChange={e=>handleChange(e,'cnh')} className="w-full border rounded p-2" /></div>
                  <div><label className="block text-sm mb-1">Categoria</label><input name="category" value={form.cnh.category} onChange={e=>handleChange(e,'cnh')} className="w-full border rounded p-2" /></div>
                  <div><label className="block text-sm mb-1">Validade</label><input type="date" name="expiry_date" value={form.cnh.expiry_date} onChange={e=>handleChange(e,'cnh')} className="w-full border rounded p-2" /></div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
